/**
 * Entrata Tour Data Source
 *
 * Launch scope: agent-guided tours only (AGENT_GUIDED slots). Self-guided is
 * handled via the block's external link token, not Entrata slot booking.
 * Fetches ~60 days of slots in one request; selectDate (today, MM/DD/YYYY)
 * anchors the API booking window.
 */

import type { PropertyCredentials, TourDataSource } from './types';
import { isoToApiDate, sortTimeSlots } from './date-utils';

const APT_SYNC_DOMAIN = 'https://app.repli360.com/';
const TIME_SLOTS_URL = `${APT_SYNC_DOMAIN}admin/entrata-time-slot`;
const NO_OF_DAYS = 60;

// Repli360 entrata-time-slot `tour_type` ↔ block key ↔ Entrata appointment subtypeId (EntrataRouter):
//   guided_tour       — AGENT_GUIDED   — 454 — Guided Tour        (active)
//   self_guided_tour  — SELF_GUIDED    — 560 — Self-Guided Tour   (future; use self_guided_tour_link for now)
//   live_video_tour   — VIRTUAL_GUIDED — 525 — Virtual Tour       (future)
const ENTRATA_SLOTS_API_TOUR_TYPE = 'AGENT_GUIDED';

/** Schedulable tour types exposed by this adapter (link-only types use content tokens). */
const ENTRATA_SCHEDULABLE_TOUR_TYPES = ['guided_tour'] as const;

function todayIsoAndApiDate(): { iso: string; apiDate: string } {
  const today = new Date();
  const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return { iso, apiDate: isoToApiDate(iso) };
}

function isWithinBookingWindow(isoDate: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const date = new Date(`${isoDate}T12:00:00`);
  const max = new Date(today);
  max.setDate(max.getDate() + NO_OF_DAYS);
  return date >= today && date <= max;
}

function parseSlotsData(data: unknown, fallbackIso: string): Record<string, string[]> {
  if (!data) return {};
  if (Array.isArray(data)) {
    return data.length > 0 ? { [fallbackIso]: data } : {};
  }
  if (typeof data !== 'object') return {};

  const slotsByDate: Record<string, string[]> = {};
  for (const [dateKey, slots] of Object.entries(data as Record<string, unknown>)) {
    if (!Array.isArray(slots) || slots.length === 0) continue;
    if (!isWithinBookingWindow(dateKey)) continue;
    slotsByDate[dateKey] = slots as string[];
  }
  return slotsByDate;
}

function datesFromSlots(slotsByDate: Record<string, string[]>): string[] {
  return Object.keys(slotsByDate)
    .filter((iso) => slotsByDate[iso]?.length > 0)
    .map(isoToApiDate);
}

export class EntrataSource implements TourDataSource {
  readonly id = 'entrata' as const;
  readonly leadRouter = 'ENTRATA' as const;
  readonly slotsCachedWithDates = true;
  readonly refetchesOnTourTypeChange = false;

  private cachedSlots: Record<string, string[]> | null = null;
  private inFlight: Promise<Record<string, string[]>> | null = null;

  async fetchCredentials(siteId: string): Promise<PropertyCredentials | null> {
    if (!siteId) return null;
    return { siteId, propertyCode: '', propertyId: '', bedrooms: {} };
  }

  async initialize(creds: PropertyCredentials): Promise<{ tourTypes: string[]; dates?: string[] }> {
    const slotsByDate = await this.fetchSlots(creds);
    return {
      tourTypes: [...ENTRATA_SCHEDULABLE_TOUR_TYPES],
      dates: datesFromSlots(slotsByDate),
    };
  }

  async fetchAvailableDates(creds: PropertyCredentials): Promise<string[]> {
    if (!creds.siteId) return [];
    const slotsByDate = await this.fetchSlots(creds);
    return datesFromSlots(slotsByDate);
  }

  async fetchTimeSlotsForDate(
    creds: PropertyCredentials,
    isoDate: string,
    _tourType: string | null,
  ): Promise<string[]> {
    if (!creds.siteId) return [];
    const slotsByDate = await this.fetchSlots(creds);
    return sortTimeSlots(slotsByDate[isoDate] ?? []);
  }

  private async fetchSlots(creds: PropertyCredentials): Promise<Record<string, string[]>> {
    if (this.cachedSlots) return this.cachedSlots;
    if (this.inFlight) return this.inFlight;

    const promise = this.doFetchSlots(creds).finally(() => {
      this.inFlight = null;
    });
    this.inFlight = promise;
    return promise;
  }

  private async doFetchSlots(creds: PropertyCredentials): Promise<Record<string, string[]>> {
    const { iso, apiDate } = todayIsoAndApiDate();

    try {
      const body = new URLSearchParams({
        site_id: creds.siteId,
        type: 'encoded',
        tour_type: ENTRATA_SLOTS_API_TOUR_TYPE,
        selectDate: apiDate,
      });

      const res = await fetch(TIME_SLOTS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!res.ok) {
        this.cachedSlots = {};
        return {};
      }

      const result = await res.json();
      const ok = result?.status === 'true' || result?.status === true;
      const slotsByDate = ok ? parseSlotsData(result?.data, iso) : {};
      this.cachedSlots = slotsByDate;
      return slotsByDate;
    } catch (err) {
      console.error('[EntrataSource] Failed to fetch time slots:', err);
      this.cachedSlots = {};
      return {};
    }
  }
}
